import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TFunction } from 'i18next';

import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { useInitWebEDIStore } from 'services/webedi/webedi';
import { setDestinationProcess, setProcessSpecificationFields } from 'stores/webedi/webedi';
import { DEFAULT_REFETCH_INTERVAL, EMPTY_FIELD } from 'support/helpers/const/const';
import { isMessageBEValidationComplete } from 'support/helpers/messages/messages';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { RouteParams } from 'support/helpers/types/types';
import { TransactionsRoutes, TransactionState } from 'support/types';
import { Page } from 'components/Page/Page';

import { useAcknowledgement } from './hooks/useAcknowledgement';
import { useIsAdminOCRTest } from './hooks/useIsAdminOCRTest';
import { revealPartyFields } from '../../helpers';
import { ExistingMessageUI } from './ExistingMessageUI/ExistingMessageUI';
import { MissingMessageUI } from './MissingMessageUI/MissingMessageUI';
import { UnknownTypePlaceholder } from './UnknownTypePlaceholder';

export function MessageViewPage() {
  useInitWebEDIStore();
  const { id: messageId } = useParams<{ id: string }>();
  const { data: currentPartner, isLoading: isLoadingCurrentPartner } = useCurrentPartner();
  const { data: currentUser } = useCurrentUser();
  const [shouldPollMessage, setShouldPollMessage] = useState(false);
  const {
    data: message,
    isError,
    isLoading: isLoadingMessage,
  } = useMessageWithProcessSpecification({
    variables: {
      messageId,
    },
    refetchInterval: shouldPollMessage ? DEFAULT_REFETCH_INTERVAL : false,
  });

  useEffect(() => {
    setShouldPollMessage(!isMessageBEValidationComplete(message?.data?.internal_state));
  }, [message?.data?.internal_state]);

  const fields = useMemo(() => {
    if (!message?.fields || !currentUser?.isAdmin) {
      return message?.fields;
    }
    return revealPartyFields(message.fields);
  }, [message?.fields, currentUser?.isAdmin]);

  useIsAdminOCRTest(messageId);
  useAcknowledgement(message?.data, currentPartner, currentUser, message?.data?.type);

  useEffect(() => {
    if (!message) {
      return;
    }

    if (message.process) {
      setDestinationProcess(message.process);
    }
    if (fields) {
      setProcessSpecificationFields(fields);
    }
  }, [message, fields]);

  if (!messageId) {
    return;
  }

  if (isError) {
    return <UnknownTypePlaceholder messageId={messageId} />;
  }

  if (isLoadingMessage || isLoadingCurrentPartner || !currentPartner) {
    /*
     * We don't return MessageViewPageUI so we can start the form with the correct initial data
     * This way we avoid rendering "0 line items" in the UI when the data is loaded but not yet debounced
     */
    return <Page isLoading>{null}</Page>;
  }

  if (!message?.data) {
    return <UnknownTypePlaceholder messageId={messageId} />;
  }

  if (message.data.state === TransactionState.missing) {
    return (
      <MissingMessageUI
        message={message.data}
        relatedTransactions={message.related_messages}
        currentPartner={currentPartner}
      />
    );
  }

  return (
    <ExistingMessageUI
      message={message?.data}
      messageOriginApp={message?.origin_app}
      relatedTransactions={message?.related_messages}
      currentPartner={currentPartner}
      processSpecFields={fields}
    />
  );
}

MessageViewPage.loader = createLoader(
  ({ params }) => useMessageWithProcessSpecification.getKey({ messageId: params.id }),
  useMessageWithProcessSpecification.queryFn,
);

type ResponseType = Awaited<ReturnType<typeof useMessageWithProcessSpecification.queryFn>>;
MessageViewPage.handle = createHandle<ResponseType, RouteParams<TransactionsRoutes.viewMessage>>((params, data) => ({
  label: (t: TFunction) =>
    t('webedi:viewDocument.breadcrumb', {
      externalReference: data?.data.external_message_ref || EMPTY_FIELD,
      documentType: data?.data.type
        ? t(`common:messageTypes.singular.${data.data.type}`)
        : t('common:messageTypes.singular.UNKNOWN'),
    }),
  to: routeToPage(TransactionsRoutes.viewMessage, { id: params.id }),
  key: `view-message`,
}));
